import {
  Component,
  OnInit,
  ViewContainerRef,
  ViewChild,
  ComponentFactoryResolver,
  OnDestroy,
  NgZone
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Location } from '@angular/common';
import { MapComponent as RPMapComponent, Geometry, Coordinate, CoordinateBoundary, Marker } from '@realpage-da/ng-map';
import { assign, includes, isEmpty } from 'lodash';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { SIZES } from '~/shared/configs';
import { SidebarComponent } from './sidebar.component';
import { MarketViewComponent, PropertyViewComponent, ViewType, MapState, LandingComponent, LandingAnchorDirective } from '../views';
import { ViewLevel, VIEW_LEVELS, resolveView, resolveZoomLevel } from '../constants';

import { ColorConstants } from '~/shared/utils/color-constants';
import { PropertyMarkerComponent, PropertyTooltipComponent } from '~features/property/components/map';
import { AnalyticsService, MarketService, PropertyService } from '~/core/services';
import { PROPERTY_TYPE, PropertyDetail } from '~/core/model';

const marketRouteParams = ['stateAbbr', 'marketName'];
const propertyRouteParams = ['propertyName', 'propertyId'];
const mapRouteParams = ['zoom', 'latitude', 'longitude'];

export const isMarketRoute = (routeParams: string[]): boolean =>
  marketRouteParams.every((m) => routeParams.includes(m));

export const isPropertyRoute = (routeParams: string[]): boolean =>
  propertyRouteParams.every((p) => routeParams.includes(p));

export const isMapRoute = (routeParams: string[]): boolean =>
  mapRouteParams.every((m) => routeParams.includes(m));

const VIEW_LEVEL_COMPONENT = {
  [VIEW_LEVELS.MARKET]: MarketViewComponent,
  [VIEW_LEVELS.PROPERTY]: PropertyViewComponent
};

const SHOW_LEADERBOARD_MODAL = {
  'market': false,
  'property': false
}

const resolveComponentByZoom = (zoom: number): ViewType => {
  return VIEW_LEVEL_COMPONENT[resolveView(zoom).name] || null;
};

export type PureReducer<State> = (current: State) => State;
export type PureReducerFactory<State> = (...args: any[]) => PureReducer<State>;

const changeZoom: PureReducerFactory<MapState> = (zoom: number) => (current: MapState) =>
  assign({}, current, { zoom, componentType: resolveComponentByZoom(zoom) });

const changeBounds: PureReducerFactory<MapState> = (bounds: CoordinateBoundary) => (current: MapState) =>
  assign({}, current, { bounds });

const changeSidebar: PureReducerFactory<MapState> = (sidebar: 'closed' | 'open' | 'full') => (current: MapState) =>
  assign({}, current, { sidebar });

@Component({
  selector: 'idap-main-view',
  templateUrl: './main-view.component.html',
  styleUrls: ['./main-view.component.scss'],
})
export class MainViewComponent implements OnInit, OnDestroy {
  @ViewChild('target', { read: ViewContainerRef }) target: ViewContainerRef;
  @ViewChild(RPMapComponent) map: RPMapComponent;
  @ViewChild(SidebarComponent) sidebar: SidebarComponent;
  @ViewChild(LandingAnchorDirective) landingAnchorDirective: LandingAnchorDirective;
  searchResponse: Subject<Marker> = new Subject<Marker>();
  openPropertyModal: Subject<PropertyDetail> = new Subject<PropertyDetail>();
  openLeaderboardModal: Subject<number> = new Subject<number>();

  iconColors: { name: string, color: string }[] = [
    { name: 'None', color: null },
    { name: 'Blue (dark)', color: ColorConstants.BLUE_DARK },
    { name: 'Blue (raul)', color: ColorConstants.BLUE_RAUL },
    { name: 'Blue (60)', color: ColorConstants.BLUE_60 },
    { name: 'Blue (30)', color: ColorConstants.BLUE_30 },
    { name: 'Charcoal', color: ColorConstants.CHARCOAL },
    { name: 'Charcoal (dark)', color: ColorConstants.CHARCOAL_DARK },
    { name: 'Charcoal (light)', color: ColorConstants.CHARCOAL_LIGHT },
    { name: 'Gray', color: ColorConstants.GRAY },
    { name: 'Gray (light)', color: ColorConstants.GRAY_LIGHT },
    { name: 'Gray (lightest)', color: ColorConstants.GRAY_LIGHTEST },
    { name: 'Gray (medium)', color: ColorConstants.GRAY_MEDIUM },
    { name: 'Gray (dark)', color: ColorConstants.GRAY_DARK },
    { name: 'Purple', color: ColorConstants.PURPLE },
    { name: 'Purple (60)', color: ColorConstants.PURPLE_60 },
    { name: 'Purple (30)', color: ColorConstants.PURPLE_30 },
    { name: 'Orange', color: ColorConstants.ORANGE },
    { name: 'Orange (60)', color: ColorConstants.ORANGE_60 },
    { name: 'Orange (30)', color: ColorConstants.ORANGE_30 },
    { name: 'Brown', color: ColorConstants.BROWN },
    { name: 'Aqua', color: ColorConstants.AQUA },
    { name: 'Aqua (dark)', color: ColorConstants.AQUA_DARK },
    { name: 'Green', color: ColorConstants.GREEN },
    { name: 'Green (light)', color: ColorConstants.GREEN_LIGHT },
    { name: 'Yellow', color: ColorConstants.YELLOW },
    { name: 'Red', color: ColorConstants.RED },
    { name: 'White', color: ColorConstants.WHITE }
  ];

  stateChanged: Subject<PureReducer<MapState>> = new Subject();
  marketMarkers: Observable<Marker[]>;
  onDestroy: Subject<void> = new Subject<void>();

  state: Observable<MapState>;
  viewLevel: ViewLevel = VIEW_LEVELS.MARKET;

  routeParams: Observable<ParamMap>;

  sidebarWidth: string = '0px';

  center: { latitude: number, longitude: number } = {
    latitude: 32.9830762,
    longitude: -96.7119411
  };

  queryParams: { latitude: number, longitude: number, zoom: number };

  bounds: Geometry[] = [
    new Geometry.Point(49.251395, -67.277873),
    new Geometry.Point(24.584207, -126.252482)
  ];

  constructor(
    public analytics: AnalyticsService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private location: Location,
    private marketService: MarketService,
    private ngZone: NgZone,
    private propertyService: PropertyService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    // Ugly workaround for market marker/grid click not loading view
    // if entering the same market, for example, after zooming out.
    // Drawback is that the MainViewComponent will be instantiated more than once
    // throughout the lifetime of the app instance.
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
  }

  ngOnInit() { }

  ngAfterViewInit() {
    this.setState();

    this.routeParams = Observable.combineLatest(this.route.paramMap, this.map.map.onIdle)
      .map(([route, _]) => route)
      .distinctUntilChanged()
      .takeUntil(this.onDestroy);

    this.routeParams
      .filter(route => route.keys.length === 0)
      .subscribe(() => {
        this.map.map.setBounds(this.bounds)
        this.showLandingPopup();
      })

    this.routeParams
      .filter(route => isPropertyRoute(route.keys))
      .map(p => p.get('propertyId'))
      .switchMap((propertyId) => this.propertyService.getPropertyInfo(propertyId, null, PROPERTY_TYPE.SUBJECT.key))
      .takeUntil(this.onDestroy)
      .subscribe(
        (propertyDetail) => {
          this.loadView(
            VIEW_LEVELS.PROPERTY,
            { latitude: +propertyDetail.latitude, longitude: +propertyDetail.longitude },
            { property: propertyDetail, showMarker: false, showPropertyModal: true },
            resolveZoomLevel(VIEW_LEVELS.PROPERTYSEARCH).min
          );
        },
        (error) => {
          error ? console.error(error) : console.error('Error loading property data.');
          this.map.map.setBounds(this.bounds);
        }
      );

    this.routeParams
      .filter(route => isMarketRoute(route.keys))
      .switchMap((route) =>
        this.marketService.getMarketCenter(route.get('stateAbbr'), route.get('marketName'))
          .catch((_) => Observable.of(null))
      )
      .takeUntil(this.onDestroy)
      .subscribe(
        (marketDetail) => {
          if (!isEmpty(marketDetail)) {
            this.loadView(
              VIEW_LEVELS.PROPERTY,
              { latitude: +marketDetail.latitude, longitude: +marketDetail.longitude },
              { showMarker: false, marketId: marketDetail.id, showLeaderboardModal: false }
            );
          } else {
            this.map.map.setBounds(this.bounds);
          }
        }
      );

    Observable.combineLatest(this.route.queryParamMap, this.map.map.onIdle)
      .map(([route, _]) => route)
      .distinctUntilChanged()
      .take(1)
      .filter(route => isMapRoute(route.keys))
      .subscribe(
        (route) => {
          const routeMap = {
            latitude: +route.get('latitude'),
            longitude: +route.get('longitude'),
            zoom: +route.get('zoom')
          }
          this.setMapView(routeMap);
        },
        () => {
          this.map.map.setBounds(this.bounds);
        }
      );

    this.updateQueryParams();

    this.landingAnchorDirective.search
      .takeUntil(this.onDestroy)
      .subscribe(params => this.onSearch(params))
  }

  ngOnDestroy() {
    this.searchResponse.complete();
    this.openPropertyModal.complete();
    this.openLeaderboardModal.complete();
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  showLandingPopup() {
    this.ngZone.run(() => {
      this.landingAnchorDirective.createLandingModalPopup(LandingComponent)
    });
  }

  updateQueryParams() {
    // zoomChange fires once on page load
    // skipping the first time that it emit
    // prevents this method from firing prematurely
    const zoomChange = this.map.map.zoomChanged.skip(1);
    const dragEnd = this.map.map.onMapDragEnd;

    Observable.merge(dragEnd, zoomChange).subscribe(() => {
      const { latitude, longitude } = this.map.map.getCenter();
      const zoom = this.map.map.getZoom();
      this.updateLocationState(
        '/main',
        `?latitude=${latitude}` +
        `&longitude=${longitude}` +
        `&zoom=${zoom}`
      )
    });
  }

  setMapView({ latitude, longitude, zoom }: {
    latitude?: number,
    longitude?: number,
    zoom?: number
  }) {
    if (latitude && longitude && zoom) {
      const center = { latitude, longitude };
      // Must be used afterInit
      this.map.map.setCenter(center);
      this.map.map.setZoom(zoom);
    }
  }

  setState() {
    this.state = Observable.merge(
      this.map.zoomChanged.map(changeZoom),
      this.map.boundsChanged.map(bounds => changeBounds(bounds)),
      this.stateChanged,
      this.sidebar.onToggle
        .startWith(this.sidebar)
        .map(sidebar => changeSidebar(sidebar.collapsed))
    )
      .scan((state: MapState, reduce) => reduce(state), {} as any)
      .map(state => this.dispatchComponent(state))
      .share();

    this.state
      .distinctUntilChanged((x, y) => x.componentType === y.componentType && x.sidebar === y.sidebar)
      .filter(state => !!state.componentRef)
      .subscribe(state => this.dispatchResize(state));
  }

  onSearch(params) {
    let zoom = resolveZoomLevel(VIEW_LEVELS.PROPERTY).min;

    if (params.property.searchType === 'market') {
      this.marketService.getMarketSlug(params.property.id)
        .subscribe(m => this.updateLocationState(`/main/${m.primaryState.toLowerCase()}/${m.urlSlug}`));
    } else {
      this.location.go('/main');
    }

    if (params.property.searchType === 'property') {
      zoom = resolveZoomLevel(VIEW_LEVELS.PROPERTYSEARCH).min;
    }

    if (params.property.searchType === 'google') {
      if (!includes(params.property.categories, 'political')
        && !includes(params.property.categories, 'postal_code')) {
        zoom = resolveZoomLevel(VIEW_LEVELS.PROPERTYSEARCH).min;
      }
    }

    if (params.property.searchType === 'property' || params.property.searchType === 'google') {
      this.loadView(
        VIEW_LEVELS.PROPERTY,
        { latitude: +params.property.latitude, longitude: +params.property.longitude },
        {
          marker: params.property,
          showMarker: params.showMarker,
          marketId: params.property.marketId || params.property.id,
          showLeaderboardModal: SHOW_LEADERBOARD_MODAL[params.property.type]
        }
        , zoom
      );
    }

    this.analytics.emitEvent('click', {
      event_category: params.property.type,
      event_label: 'search',
      value: params.property.id ? params.property.id : 0
    });
  }

  /*
    When panning/zooming the map, update current history entry through the Location service.
    This has the effect of the user not being able to back button to previous pan/zoom states
    and the location change will not be present on Router.events, thus no GA pageview.

    Otherwise, any map navigation changes should be part of Router.events and register a pageview.
  */
  updateLocationState(path: string, query = ''): void {
    query
      ? this.location.replaceState(path, query)
      : this.router.navigateByUrl(path);
  }

  private dispatchComponent(state: MapState): MapState {
    if (state.componentRef && state.componentRef.instance instanceof state.componentType)
      return state;

    if (!state.componentType)
      return state;

    if (state.componentRef) {
      state.componentRef.destroy();
      state.componentRef.changeDetectorRef.detach();
      state.componentRef.instance.parent = null;
      state.componentRef.instance.state = null;
    }

    state.componentRef = this.target.createComponent(this.componentFactoryResolver.resolveComponentFactory(state.componentType));
    state.componentRef.instance.map = this.map;
    state.componentRef.instance.parent = this;
    state.componentRef.instance.state = this.state;
    state.componentRef.changeDetectorRef.detectChanges();

    this.viewLevel = state.componentType.viewLevel;

    return state;
  }

  private dispatchResize(state: MapState): MapState {
    const absoluteMax = window.innerWidth - SIZES.characterHeight(2);

    const width = Math.min(absoluteMax, this.sidebar.collapsed === 'open'
      ? state.componentRef.instance.halfWidth
      : this.sidebar.collapsed === 'full'
        ? state.componentRef.instance.maxWidth
        : 0);

    requestAnimationFrame(() => {
      this.sidebar.setWidth(width);
      this.sidebarWidth = `-${width || 0}px`;
    });

    return state;
  }

  loadView(level: ViewLevel, center?: Coordinate, params?: any, zoom?: number, bounds?: CoordinateBoundary) {
    zoom = zoom || resolveZoomLevel(level).min;
    this.map.map.setZoom(zoom, true);

    if (center) this.map.map.setCenter(center);
    bounds = bounds || this.map.map.getBounds();

    const componentType = VIEW_LEVEL_COMPONENT[level];

    this.stateChanged.next(state => assign({}, state, {
      zoom,
      componentType,
      params,
      center: center || state.bounds.midpoint,
      bounds
    }));

    if (!!params.showMarker) {
      const marker = {
        id: +params.marker.propertyId,
        geometry: new Geometry.Point(+params.marker.latitude, +params.marker.longitude),
        data: params.marker,
        options: {
          icon: {
            component: PropertyMarkerComponent
          },
          tooltip: PropertyTooltipComponent
        }
      };
      this.searchResponse.next(marker);
    }

    if (!!params.showPropertyModal) {
      this.openPropertyModal.next(params.property)
    }

    if (!!params.showLeaderboardModal) {
      this.openLeaderboardModal.next(params.marketId)
    }

    return this;
  }
}



// WEBPACK FOOTER //
// ./src/app/main/components/main-view.component.ts